import React, { useState } from "react";
import { useI18n } from "../../lib/i18n.jsx";
import { PRODUCTS } from "../../lib/products.js";
import { track } from "../../lib/analytics.js";

const SYMPTOMS = [
  { key: "clorosis", productId: "hierro-6-48", icon: "fa-leaf" },
  { key: "cuaje", productId: "nuster-flower", icon: "fa-seedling" },
  { key: "calibre", productId: "engorde-complet", icon: "fa-apple-whole" },
  { key: "estres", productId: "nautic", icon: "fa-sun-plant-wilt" },
  { key: "nematodos", productId: "nedeska-plus", icon: "fa-bug" },
  { key: "suelo", productId: "cuak-g-10", icon: "fa-mound" },
];

export default function Diagnostico({ onSelectProduct, onRequestQuote, onAskFlora }) {
  const { t, lang } = useI18n();
  const [active, setActive] = useState(null);

  const product = active ? PRODUCTS.find((p) => p.id === active.productId) : null;

  const handleSelect = (s) => {
    track("diagnostico_symptom_select", { symptom: s.key, product_id: s.productId });
    setActive(s);
  };

  return (
    <section id="diagnostico" className="py-24 lg:py-32 bg-[#133322]" data-testid="diagnostico-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <div className="eu-data-label mb-4 !text-[#9ead9b]">
            <i className="fa-solid fa-stethoscope mr-2"></i>
            {t.diagnostico.eyebrow}
          </div>
          <h2 className="font-display text-4xl lg:text-5xl text-[#f7f7f4] leading-tight">
            {t.diagnostico.title}
          </h2>
          <p className="mt-4 text-[#9ead9b] text-base lg:text-lg">{t.diagnostico.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div className="flex flex-col gap-px bg-[#ffffff1a] rounded-sm overflow-hidden" role="tablist">
            {SYMPTOMS.map((s) => {
              const selected = active?.key === s.key;
              return (
                <button
                  key={s.key}
                  role="tab"
                  aria-selected={selected}
                  onClick={() => handleSelect(s)}
                  data-testid={`diagnostico-symptom-${s.key}`}
                  className={`text-left px-5 py-4 flex items-center gap-4 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b45f3e] focus-visible:ring-inset ${
                    selected
                      ? "bg-[#b45f3e] text-[#f7f7f4] font-medium"
                      : "bg-[#133322] text-[#f7f7f4] hover:bg-[#1c4d33] hover:pl-7"
                  }`}
                >
                  <i className={`fa-solid ${s.icon} w-5 text-center ${selected ? "text-[#f7f7f4]" : "text-[#9ead9b]"}`} aria-hidden="true"></i>
                  <span className="text-[0.95rem]">{t.diagnostico.symptoms[s.key]}</span>
                </button>
              );
            })}
          </div>

          <div className="bg-[#1c4d33] rounded-sm p-6 lg:p-8 min-h-[340px] flex flex-col" aria-live="polite">
            {!product ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <i className="fa-solid fa-hand-pointer text-3xl text-[#9ead9b] mb-4"></i>
                <p className="text-[#9ead9b] italic">{t.diagnostico.idle}</p>
              </div>
            ) : (
              <>
                <div className="flex gap-5 items-start">
                  {product.image && (
                    <div className="h-24 w-24 shrink-0 bg-[#ffffff0d] rounded-sm p-2 flex items-center justify-center">
                      <img src={product.image} alt={product.name[lang]} className="max-h-full object-contain" loading="lazy" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-mono-eu text-[0.65rem] tracking-[0.18em] uppercase text-[#b45f3e]">
                      {product.category[lang]}
                      {product.npk ? ` · ${product.npk}` : ""}
                    </div>
                    <h3 className="font-display text-2xl text-[#f7f7f4] mt-1 leading-snug">{product.name[lang]}</h3>
                    <p className="text-[#9ead9b] text-sm mt-2">{product.tagline[lang]}</p>
                  </div>
                </div>

                {product.dosage?.fertigation?.[lang] && (
                  <div className="mt-6 pt-5 border-t border-[#ffffff1a]">
                    <div className="font-mono-eu text-[0.6rem] tracking-[0.18em] uppercase text-[#b45f3e] mb-1.5">
                      {t.productDetail.fertigation}
                    </div>
                    <p className="text-[#f7f7f4] text-sm leading-relaxed opacity-90">
                      {product.dosage.fertigation[lang]}
                    </p>
                  </div>
                )}

                <div className="mt-auto pt-6 flex flex-wrap gap-3">
                  <button onClick={() => onSelectProduct?.(product)} className="px-5 py-2.5 rounded-sm bg-[#f7f7f4] text-[#133322] text-sm font-medium hover:bg-white transition">
                    {t.products.view_detail}
                  </button>
                  <button onClick={() => onRequestQuote?.(product)} className="px-5 py-2.5 rounded-sm bg-[#b45f3e] text-[#f7f7f4] text-sm font-medium hover:bg-[#a0532f] transition">
                    {t.products.request_quote}
                  </button>
                  <button onClick={() => onAskFlora?.(product, t.diagnostico.symptoms[active.key])} className="px-5 py-2.5 rounded-sm border border-[#9ead9b] text-[#9ead9b] text-sm hover:bg-[#ffffff0d] transition">
                    {t.diagnostico.ask_flora}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="mt-10 text-[#9ead9b] text-xs max-w-3xl leading-relaxed opacity-80">
          {t.diagnostico.disclaimer}
        </p>
      </div>
    </section>
  );
}
